import { CircleNotchIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useCreateWorkspace } from "@/lib/api";
import { logger } from "@/lib/logger";

import { workspaceErrorMessage } from "./utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** A new client workspace with its first engagement; opens the new workspace's page. */
export function CreateWorkspaceDialog({ open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New workspace</DialogTitle>
          <DialogDescription>
            One workspace per client. It starts with its first engagement.
          </DialogDescription>
        </DialogHeader>
        {/* Remounted on every open, so the form starts empty. */}
        {open && <CreateWorkspaceForm onCancel={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function CreateWorkspaceForm({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const createMutation = useCreateWorkspace();
  const [name, setName] = useState("");
  const [engagementName, setEngagementName] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim() || !engagementName.trim()) {
      setError("Enter both the workspace name and the first engagement's name.");
      return;
    }
    setError("");
    createMutation.mutate(
      { name: name.trim(), engagementName: engagementName.trim() },
      {
        onSuccess: (created) => {
          if (created) {
            void navigate(`/admin/workspaces/${created.workspace.id}`);
          }
        },
        onError: (err) => {
          logger.debug("[admin] create workspace failed:", err);
          setError(workspaceErrorMessage(err));
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="workspaceName" className="text-foreground text-sm font-medium">
          Workspace name
        </label>
        <Input
          id="workspaceName"
          value={name}
          maxLength={200}
          placeholder="The client's name"
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <label htmlFor="engagementName" className="text-foreground text-sm font-medium">
          First engagement
        </label>
        <Input
          id="engagementName"
          value={engagementName}
          maxLength={200}
          placeholder="The process being redesigned"
          onChange={(e) => setEngagementName(e.target.value)}
        />
      </div>
      {error && (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={createMutation.isPending}>
          {createMutation.isPending ? <CircleNotchIcon className="h-4 w-4 animate-spin" /> : null}
          Create workspace
        </Button>
      </DialogFooter>
    </form>
  );
}
